// «Поделиться» — puntea dintre screener și comunitate.
//
// Din videoclipul lor: de pe grafic trimiți moneda fie în chat, fie direct într-o idee
// de tranzacționare, iar **captura se atașează automat, cu tot cu desenele tale**. Asta
// e ce leagă cele două jumătăți: altfel ai grafic într-un loc și discuție în altul, iar
// oamenii ajung să dea capturi pe alte canale.

import { el, bus, fmtPrice } from '../core/util.js';
import { icon } from './icons.js';
import { session } from '../core/session.js';
import { openAuth } from './auth-modal.js';
import { modal, toast } from './components.js';

/** Captura graficului ca data URI, cu desenele și nivelurile de pe el. */
export function chartShot(chart) {
  try {
    const canvas = chart.chart.takeScreenshot();
    // JPEG, nu PNG: baza e SQLite pe un VM mic, iar diferența e de câteva ori.
    return canvas.toDataURL('image/jpeg', 0.72);
  } catch (err) {
    console.warn('[share] captura a eșuat', err);
    return '';
  }
}

function pretCurent(chart) {
  const last = chart?.bars?.[chart.bars.length - 1];
  return last ? last.close : 0;
}

/** Trimite moneda în chat, cu descriere și captură. */
export function shareToChat(panel) {
  if (!session.authenticated) { openAuth(); return; }
  const shot = chartShot(panel.chart);

  const textEl = el('textarea', {
    class: 'idea-form-text', rows: 3,
    placeholder: 'Что здесь интересного? Пара слов для остальных…',
  });

  const body = el('div', { class: 'share-box' }, [
    el('div', { class: 'share-sym' }, [
      el('span', { class: 'idea-symbol', text: panel.symbol }),
      el('span', { class: 'share-price', text: fmtPrice(pretCurent(panel.chart)) }),
    ]),
    shot ? el('img', { class: 'share-preview', src: shot, alt: '' }) : null,
    textEl,
  ]);

  const dlg = modal({
    title: 'Отправить в чат',
    width: 480,
    body,
    actions: [
      { label: 'Отмена', onClick: (c) => c() },
      {
        label: 'Отправить', primary: true,
        onClick: (c) => {
          // Trimiterea merge prin socketul secțiunii de chat dacă e deschisă; altfel
          // deschidem chatul și lăsăm mesajul în așteptare, ca să nu se piardă.
          bus.emit('chat:send', { text: textEl.value, symbol: panel.symbol, shot });
          c();
          toast('Отправлено в чат', 'success');
        },
      },
    ],
  });
  setTimeout(() => textEl.focus(), 30);
  return dlg;
}

/**
 * Publica graficul in lenta, cu tot cu desenele de pe el.
 *
 * Butonul asta scurteaza drumul de la „vad ceva" la „am spus altora" la o singura
 * apasare. Cand publicarea cere sa mergi in alta parte, sa faci o captura si sa o
 * incarci, nu publica aproape nimeni.
 */
export function shareToFeed(panel) {
  if (!session.authenticated) { openAuth(); return; }
  const shot = chartShot(panel.chart);
  const textEl = el('textarea', {
    class: 'idea-form-text', rows: 3,
    placeholder: 'Что видите на графике? Пара слов для ленты…',
  });

  modal({
    title: 'Опубликовать в ленте',
    width: 480,
    body: el('div', { class: 'share-box' }, [
      el('div', { class: 'share-sym' }, [
        el('span', { class: 'idea-symbol', text: panel.symbol }),
        el('span', { class: 'share-price', text: fmtPrice(pretCurent(panel.chart)) }),
      ]),
      shot ? el('img', { class: 'share-preview', src: shot, alt: '' }) : null,
      textEl,
    ]),
    actions: [
      { label: 'Отмена', onClick: (c) => c() },
      {
        label: 'Опубликовать', primary: true,
        onClick: async (c) => {
          const text = textEl.value.trim();
          try {
            const resp = await fetch('/api/feed', {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              // Moneda merge pe langa text, ca publicatia sa intre in sentimentul ei
              // chiar daca omul n-a scris niciun „$".
              body: JSON.stringify({ text, shot, symbol: panel.symbol }),
            });
            const d = await resp.json().catch(() => ({}));
            if (!resp.ok) throw new Error(d.detail || `Ошибка ${resp.status}`);
            toast('Опубликовано в ленте', 'success');
          } catch (err) { toast(err.message, 'error'); }
          c();
        },
      },
    ],
  });
  setTimeout(() => textEl.focus(), 30);
}


/** Deschide formularul de idee, precompletat din graficul curent. */
export function shareToIdea(panel) {
  if (!session.authenticated) { openAuth(); return; }
  bus.emit('idea:compose', {
    symbol: panel.symbol,
    exchange: panel.exchange,
    market: panel.market,
    tf: panel.tf,
    price: pretCurent(panel.chart),
    shot: chartShot(panel.chart),
  });
}

/** Meniul de sub buton. */
export function openShareMenu(panel, anchor) {
  document.querySelector('.share-menu')?.remove();
  const menu = el('div', { class: 'share-menu' }, [
    el('button', { class: 'share-item', onclick: () => { menu.remove(); shareToChat(panel); } },
      [icon('comments', 13), el('span', { text: 'В чат' })]),
    el('button', { class: 'share-item', onclick: () => { menu.remove(); shareToFeed(panel); } },
      [icon('rows', 13), el('span', { text: 'В ленту' })]),
    el('button', { class: 'share-item', onclick: () => { menu.remove(); shareToIdea(panel); } },
      [icon('lightbulb', 13), el('span', { text: 'В торговую идею' })]),
    el('button', { class: 'share-item', onclick: () => {
      menu.remove();
      // Camera monedei: discutia lipita de ce ai pe ecran. Adresa poarta moneda,
      // fiindca navigarea reincarca pagina si un eveniment nu i-ar supravietui.
      location.href = `/chat/?symbol=${encodeURIComponent(panel.symbol)}`;
    } }, [icon('comments', 13), el('span', { text: 'Обсуждение монеты' })]),
    el('button', { class: 'share-item', onclick: () => { menu.remove(); panel.shareLink(); } },
      [icon('share', 13), el('span', { text: 'Скопировать ссылку' })]),
    el('button', { class: 'share-item', onclick: () => { menu.remove(); panel.screenshot(); } },
      [icon('camera', 13), el('span', { text: 'Скриншот в буфер' })]),
  ]);
  document.body.append(menu);

  // Meniul se ancorează sub buton, dar nu iese din ecran pe marginea dreaptă.
  const r = anchor.getBoundingClientRect();
  menu.style.top = `${r.bottom + 6}px`;
  menu.style.left = `${Math.min(r.left, window.innerWidth - menu.offsetWidth - 12)}px`;

  setTimeout(() => {
    const off = (e) => {
      if (!menu.contains(e.target)) { menu.remove(); document.removeEventListener('mousedown', off); }
    };
    document.addEventListener('mousedown', off);
  }, 0);
}
